import { fetchJSON as fetchAPI } from './client';

export type SOPCycle = {
  id: string;
  name: string;
  period: string;
  status: string;
  alignment_score: number;
  consensus_score: number;
  enterprise_readiness_score: number;
  planning_maturity_score: number;
  open_exceptions: number;
  open_conflicts: number;
  pending_decisions: number;
  demand_plan_id?: string;
  supply_plan_id?: string;
  financial_plan_id?: string;
};

export type ConsensusRecord = {
  id: string;
  function_area: string;
  status: string;
  reviewer?: string;
  assumptions?: string;
  conflict_count: number;
  resolved_count: number;
  notes?: string;
};

export type PlanningException = {
  id: string;
  exception_type: string;
  severity: string;
  business_impact: string;
  root_cause: string;
  recommended_resolution: string;
  source_domain: string;
  target_domain: string;
  resolved: boolean;
};

export type ExecutiveDecision = {
  id: string;
  title: string;
  decision_owner: string;
  business_justification: string;
  alternative_options?: string;
  financial_impact: number;
  risk_impact?: string;
  approval_status: string;
};

export type MeetingRecord = {
  id: string;
  title: string;
  meeting_type: string;
  scheduled_at: string;
  attendees: string[];
  agenda?: string;
  minutes?: string;
  decisions_count: number;
  action_items_count: number;
  status: string;
};

export type ActionItem = {
  id: string;
  title: string;
  owner: string;
  due_date?: string;
  status: string;
  priority: string;
};

export type PlanAlignment = {
  id: string;
  source_domain: string;
  target_domain: string;
  alignment_pct: number;
  mismatch_pct: number;
  gap_description?: string;
  recommended_action?: string;
};

export type SOPRecommendation = {
  id: string;
  type: string;
  title: string;
  description: string;
  business_explanation: string;
  confidence: number;
  expected_business_outcome: string;
  expected_financial_impact: number;
  expected_operational_impact?: string;
  auto_execute: boolean;
  status: string;
};

export type SOPDashboard = {
  active_cycles: number;
  alignment_score: number;
  consensus_score: number;
  forecast_accuracy: number;
  service_level: number;
  revenue_achievement: number;
  working_capital: number;
  inventory_health: number;
  supply_reliability: number;
  decision_cycle_time_days: number;
  planning_maturity_score: number;
  enterprise_readiness_score: number;
  open_exceptions: number;
  open_conflicts: number;
  pending_recommendations: number;
  consensus_rate: number;
  conflict_frequency: number;
};

const API_BASE = import.meta.env.VITE_SOPPLANNING_API ?? '/api/v1';

function sopFetch<T>(path: string, init?: RequestInit): Promise<T> {
  return fetchAPI<T>(API_BASE, path, init);
}

export const sopPlanningApi = {
  dashboard: () => sopFetch<SOPDashboard>('/sop/dashboard'),
  listCycles: () => sopFetch<SOPCycle[]>('/sop'),
  listConsensus: (cycleId?: string) => sopFetch<ConsensusRecord[]>(`/sop/consensus${cycleId ? `?cycle_id=${cycleId}` : ''}`),
  listExceptions: (cycleId?: string) => sopFetch<PlanningException[]>(`/sop/exceptions${cycleId ? `?cycle_id=${cycleId}` : ''}`),
  listDecisions: (cycleId?: string) => sopFetch<ExecutiveDecision[]>(`/sop/decisions${cycleId ? `?cycle_id=${cycleId}` : ''}`),
  listMeetings: (cycleId?: string) => sopFetch<MeetingRecord[]>(`/sop/meetings${cycleId ? `?cycle_id=${cycleId}` : ''}`),
  listActions: (cycleId?: string) => sopFetch<ActionItem[]>(`/sop/actions${cycleId ? `?cycle_id=${cycleId}` : ''}`),
  listAlignments: (cycleId?: string) => sopFetch<PlanAlignment[]>(`/sop/alignments${cycleId ? `?cycle_id=${cycleId}` : ''}`),
  listRecommendations: (cycleId?: string) => sopFetch<SOPRecommendation[]>(`/sop/recommendations${cycleId ? `?cycle_id=${cycleId}` : ''}`),
  approveCycle: (cycleId: string) => sopFetch<SOPCycle>('/sop/approve', { method: 'POST', body: JSON.stringify({ cycle_id: cycleId }) }),
};
